"use client";

import * as React from "react";
import {
  Bar,
  BarChart,
  BarProps,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { ChartLine } from "lucide-react";
import { cx } from "class-variance-authority";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTimeframeTabContext } from "@/providers/timeframe-tab-context";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import { DSContractState } from "@myth-finance/dualstake-ts-sdk";
import {
  addStakedMetaToEvents,
  useHistoryEvents,
} from "@/hooks/use-history-events";
import { DAY, MONTH, WEEK } from "@/lib/constants";
import { formatNumber, N, shortenNumber } from "@/lib/utils";

const chartConfig = {
  staked: {
    label: "Total Stake",
  },
} satisfies ChartConfig;

function toYMD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const MinBar: React.FC<BarProps> = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  fill,
  // @ts-ignore
  staked,
}) => {
  const minHeight = 2;
  width = Number(width)
  height = Number(height)
  const adjustedHeight = Math.max(height, minHeight);
  const adjustedY = height < minHeight ? Number(y) - (minHeight - height) : y;
  let actualFill = fill;
  const [min, max] = staked as [number, number];
  if (max < 30000000000) actualFill = "hsl(var(--destructive))";
  else if (min < 30000000000) actualFill = "#ffba00";
  return (
    <rect
      x={x}
      y={adjustedY}
      width={width}
      height={adjustedHeight}
      fill={actualFill}
      rx={2}
    />
  );
};

export function StakedChart({
  cState,
  className,
}: {
  cState: DSContractState;
  className?: string;
}) {
  const { activeTab, setActiveTab } = useTimeframeTabContext();
  const {
    error,
    isPending,
    refetch,
    data: appEvents,
  } = useHistoryEvents(cState?.appId);

  const filteredData = React.useMemo(() => {
    if (!appEvents?.events.length) return { data: [], min: 0, max: 0 };

    const events = addStakedMetaToEvents({
      events: appEvents.events,
      mode: "GLOBAL",
      activityEventNames: new Set(["mint", "redeem"]),
    });

    const dateThreshold =
      activeTab === "1D" ? DAY : activeTab === "1W" ? WEEK : MONTH;

    const dateCutoff = Date.now() / 1000 - dateThreshold;

    let filtered = events;
    if (activeTab !== "ALL") {
      // intentionally go outside range to ensure we have some data
      const filteredPlusInitIdx = events.findLastIndex(
        (e) => e.outerTransaction.roundTime! < dateCutoff
      );
      
      filtered = filteredPlusInitIdx > -1 ? events.slice(filteredPlusInitIdx) : events
    }

    let rangeMin = Infinity;
    let rangeMax = -Infinity;

    const objDates = filtered.reduce((byDate, e, i, all) => {
      const date = toYMD(new Date(N(e.outerTransaction.roundTime!) * 1000));
      const prevDate =
        !byDate[date] && i > 0
          ? toYMD(new Date(N(all[i - 1].outerTransaction.roundTime!) * 1000))
          : undefined;
      // continue with previous day's min/max range if not first
      let [min, max] =
        byDate[date] ??
        (prevDate
          ? [byDate[prevDate][2], byDate[prevDate][2]]
          : [Infinity, -Infinity]);
        // console.log(date, min, max)
      const value = e.meta?.staked!;
      if (value < min) {
        min = value;
        if (value < rangeMin) rangeMin = value;
      }
      if (value > max) {
        max = value;
        if (value > rangeMax) rangeMax = value;
      }
      byDate[date] = [min, max, value];
      return byDate;
    }, {} as Record<string, [number, number, number]>);

    // fill in missing day data, carry lastValue fwd
    let lastValue = Object.values(objDates)[0][2];
    const first =
      activeTab === "ALL"
        ? +new Date(Object.keys(objDates)[0])
        : Math.max(dateCutoff * 1000, N(filtered[0].outerTransaction.roundTime) * 1000);
    const last = +new Date(toYMD(new Date())+" 23:59:59");
    // console.log({ first, last });
    for (let date = first; date <= last; date += DAY * 1000) {
      const ymd = toYMD(new Date(date));
      // console.log("ymd", ymd);
      if (!objDates[ymd]) {
        objDates[ymd] = [lastValue, lastValue, lastValue];
        // console.log("Miussing", ymd);
      } else {
        lastValue = objDates[ymd][2];
        // console.log("have", ymd, "last value", lastValue / 1e6);
      }
    }

    const firstYMD = toYMD(new Date(first));
    const firstInData = Object.keys(objDates)[0];
    // delete fill-in date if needed
    if (activeTab !== "ALL" && firstYMD !== firstInData)
      delete objDates[firstInData];

    const data = Object.entries(objDates)
      .map(([k, v]) => ({ date: k, staked: [v[0], v[1]] }))
      .sort(({ date: a }, { date: b }) => (a < b ? -1 : 1));

    return { data, min: rangeMin, max: rangeMax };
  }, [activeTab, appEvents?.round]);

  return (
    <Card
      className={cx("w-[350px] max-w-[100%] relative self-stretch pb-0", className)}
    >
      <CardHeader className="opacity-0 animate-fade-in-2">
        <CardTitle className="flex items-center gap-1">
          <ChartLine className="relative -top-[2px] inline" />
          Total Stake
        </CardTitle>
      </CardHeader>
      <CardContent className="opacity-0 animate-fade-in-3 min-h-32">
        <WithErrorAndLoading
          mini={true}
          loading={isPending}
          error={error?.message}
          retry={refetch}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full mb-3 -mt-2 grid-cols-4">
              <TabsTrigger value="1D">24H</TabsTrigger>
              <TabsTrigger value="1W">1W</TabsTrigger>
              <TabsTrigger value="1M">1M</TabsTrigger>
              <TabsTrigger value="ALL">ALL</TabsTrigger>
            </TabsList>
          </Tabs>
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[250px] w-full -mb-2"
          >
            <BarChart data={filteredData.data}>
              <CartesianGrid />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(date, [{ value }]) => {
                      const [min, max] = value as [number, number];
                      const minDanger = min < 30000000000;
                      const maxDanger = max < 30000000000;
                      const label = maxDanger
                        ? "No rewards"
                        : minDanger
                        ? "Partial rewards"
                        : "";
                      const className = maxDanger
                        ? "text-destructive"
                        : minDanger
                        ? "text-amber-500"
                        : "";

                      return (
                        <>
                          {new Date(date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                          })}{" "}
                          {label ? (
                            <>
                              - <span className={className}>{label}</span>
                            </>
                          ) : null}
                        </>
                      );
                    }}
                    formatter={(value) => {
                      const [min, max] = value as [number, number];
                      const minDanger = min < 30000000000;
                      const maxDanger = max < 30000000000;
                      const minClassName = maxDanger
                        ? "text-destructive"
                        : minDanger
                        ? "text-amber-500"
                        : "text-primary";
                      const maxClassName = maxDanger ? "text-destructive" : "text-primary"
                      return (
                        <>
                          <span className={minClassName}>
                            {formatNumber(min, 6, 0)} ALGO
                          </span>
                          -
                          <span className={maxClassName}>
                            {formatNumber(max, 6, 0)} ALGO
                          </span>
                        </>
                      );
                    }}
                    indicator="dot"
                  />
                }
              />
              <YAxis
                width={45}
                domain={[filteredData.min * 0.9, filteredData.max * 1.1]}
                tickFormatter={(value) => shortenNumber(value / 1e6)}
              />
              <Bar
                dataKey="staked"
                fill="hsl(var(--primary))"
                stroke="hsl(var(--primary))"
                // @ts-ignore
                shape={<MinBar />}
              />
              <ReferenceLine y={30000000000} stroke="hsl(var(--muted-foreground))" strokeDasharray="1 4" />
            </BarChart>
          </ChartContainer>
        </WithErrorAndLoading>
      </CardContent>
    </Card>
  );
}
